/**
 * Role Reference Autocomplete Component
 * Provides @ mention autocomplete functionality for role references
 */

interface Role {
    id: number;
    name: string;
}

interface AutocompleteState {
    isActive: boolean;
    searchText: string;
    searchStartPos: number;
    selectedIndex: number;
    roles: Role[];
    filteredRoles: Role[];
    textarea: HTMLTextAreaElement | null;
}

// Map to store state for each textarea
const textareaStates = new Map<HTMLTextAreaElement, AutocompleteState>();
let dropdownElement: HTMLElement | null = null;
let activeTextarea: HTMLTextAreaElement | null = null;

/**
 * Initialize autocomplete for a textarea
 */
export function initAutocomplete(textarea: HTMLTextAreaElement, roles: Role[]) {
    // Create state for this textarea
    const state: AutocompleteState = {
        isActive: false,
        searchText: '',
        searchStartPos: -1,
        selectedIndex: 0,
        roles: roles,
        filteredRoles: [],
        textarea: textarea
    };
    
    textareaStates.set(textarea, state);

    // Create dropdown element if it doesn't exist
    if (!dropdownElement) {
        dropdownElement = document.createElement('div');
        dropdownElement.className = 'role-autocomplete-dropdown';
        dropdownElement.style.display = 'none';
        document.body.appendChild(dropdownElement);
    }

    // Attach event listeners with the textarea as context
    const handleInputBound = (e: Event) => handleInput(e, textarea);
    const handleKeydownBound = (e: KeyboardEvent) => handleKeydown(e, textarea);
    const handleBlurBound = () => handleBlur(textarea);
    
    textarea.addEventListener('input', handleInputBound);
    textarea.addEventListener('keydown', handleKeydownBound);
    textarea.addEventListener('blur', handleBlurBound);
    
    // Store cleanup functions
    (textarea as any).__autocompleteCleanup = () => {
        textarea.removeEventListener('input', handleInputBound);
        textarea.removeEventListener('keydown', handleKeydownBound);
        textarea.removeEventListener('blur', handleBlurBound);
        textareaStates.delete(textarea);
    };
}

/**
 * Clean up autocomplete
 */
export function destroyAutocomplete(textarea?: HTMLTextAreaElement) {
    if (textarea) {
        // Clean up specific textarea
        if ((textarea as any).__autocompleteCleanup) {
            (textarea as any).__autocompleteCleanup();
        }
    } else {
        // Clean up all textareas
        textareaStates.forEach((state, textarea) => {
            if ((textarea as any).__autocompleteCleanup) {
                (textarea as any).__autocompleteCleanup();
            }
        });
        textareaStates.clear();
        
        if (dropdownElement) {
            dropdownElement.remove();
            dropdownElement = null;
        }
    }
    
    activeTextarea = null;
}

/**
 * Handle input events
 */
function handleInput(event: Event, textarea: HTMLTextAreaElement) {
    const state = textareaStates.get(textarea);
    if (!state) return;
    
    activeTextarea = textarea;

    const cursorPos = textarea.selectionStart;
    const text = textarea.value;

    // Find the last @ before cursor
    let atPos = -1;
    for (let i = cursorPos - 1; i >= 0; i--) {
        if (text[i] === '@') {
            // Check if @ is at start or preceded by whitespace
            if (i === 0 || /\s/.test(text[i - 1])) {
                atPos = i;
                break;
            }
        }
        // If we hit whitespace before finding @, stop
        if (/\s/.test(text[i])) {
            break;
        }
    }

    // If @ found and cursor is after it
    if (atPos !== -1 && cursorPos > atPos) {
        const searchText = text.substring(atPos + 1, cursorPos);

        // Only show dropdown if search text is 2+ chars (or empty for initial @)
        if (searchText.length >= 2 || searchText.length === 0) {
            state.isActive = true;
            state.searchText = searchText;
            state.searchStartPos = atPos;
            state.selectedIndex = 0;

            // Filter roles
            if (searchText.length === 0) {
                state.filteredRoles = [...state.roles];
            } else {
                state.filteredRoles = state.roles.filter(role =>
                    role.name.toLowerCase().includes(searchText.toLowerCase())
                );
            }

            if (state.filteredRoles.length > 0) {
                showDropdown(textarea, state);
            } else {
                hideDropdown();
            }
        } else {
            hideDropdown();
        }
    } else {
        hideDropdown();
    }
}

/**
 * Handle keyboard events
 */
function handleKeydown(event: KeyboardEvent, textarea: HTMLTextAreaElement) {
    const state = textareaStates.get(textarea);
    if (!state || !state.isActive || !dropdownElement) return;
    
    activeTextarea = textarea;

    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault();
            state.selectedIndex = Math.min(state.selectedIndex + 1, state.filteredRoles.length - 1);
            updateDropdownSelection(state);
            break;

        case 'ArrowUp':
            event.preventDefault();
            state.selectedIndex = Math.max(state.selectedIndex - 1, 0);
            updateDropdownSelection(state);
            break;

        case 'Enter':
            if (state.filteredRoles.length > 0) {
                event.preventDefault();
                selectRole(state.filteredRoles[state.selectedIndex], textarea, state);
            }
            break;

        case 'Escape':
            event.preventDefault();
            hideDropdown();
            break;

        case 'Tab':
            if (state.filteredRoles.length > 0) {
                event.preventDefault();
                selectRole(state.filteredRoles[state.selectedIndex], textarea, state);
            }
            break;
    }
}

/**
 * Handle blur events (with delay to allow clicks)
 */
function handleBlur(textarea: HTMLTextAreaElement) {
    setTimeout(() => {
        hideDropdown();
    }, 200);
}

/**
 * Show the autocomplete dropdown
 */
function showDropdown(textarea: HTMLTextAreaElement, state: AutocompleteState) {
    if (!dropdownElement) return;

    // Position dropdown below cursor
    const rect = textarea.getBoundingClientRect();
    const coords = getCaretCoordinates(textarea);

    dropdownElement.style.display = 'block';
    dropdownElement.style.position = 'absolute';
    dropdownElement.style.left = `${rect.left + coords.left}px`;
    dropdownElement.style.top = `${rect.top + coords.top + 20}px`; // 20px below cursor

    // Render dropdown content
    renderDropdown(state);
}

/**
 * Hide the autocomplete dropdown
 */
function hideDropdown() {
    if (dropdownElement) {
        dropdownElement.style.display = 'none';
    }
    if (activeTextarea) {
        const state = textareaStates.get(activeTextarea);
        if (state) {
            state.isActive = false;
            state.searchText = '';
            state.searchStartPos = -1;
            state.selectedIndex = 0;
            state.filteredRoles = [];
        }
    }
}

/**
 * Render dropdown content
 */
function renderDropdown(state: AutocompleteState) {
    if (!dropdownElement) return;

    dropdownElement.innerHTML = state.filteredRoles
        .map((role, index) => {
            const isSelected = index === state.selectedIndex;
            return `
                <div class="autocomplete-item ${isSelected ? 'selected' : ''}" 
                     data-role-id="${role.id}"
                     data-index="${index}">
                    ${escapeHtml(role.name)}
                </div>
            `;
        })
        .join('');

    // Attach click handlers
    dropdownElement.querySelectorAll('.autocomplete-item').forEach((item, index) => {
        item.addEventListener('mousedown', (e) => {
            e.preventDefault(); // Prevent blur
            if (activeTextarea) {
                const activeState = textareaStates.get(activeTextarea);
                if (activeState) {
                    selectRole(state.filteredRoles[index], activeTextarea, activeState);
                }
            }
        });

        item.addEventListener('mouseenter', () => {
            state.selectedIndex = index;
            updateDropdownSelection(state);
        });
    });
}

/**
 * Update dropdown visual selection
 */
function updateDropdownSelection(state: AutocompleteState) {
    if (!dropdownElement) return;

    dropdownElement.querySelectorAll('.autocomplete-item').forEach((item, index) => {
        if (index === state.selectedIndex) {
            item.classList.add('selected');
        } else {
            item.classList.remove('selected');
        }
    });

    // Scroll selected item into view
    const selectedItem = dropdownElement.querySelector('.autocomplete-item.selected');
    if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest' });
    }
}

/**
 * Select a role and insert the reference
 */
function selectRole(role: Role, textarea: HTMLTextAreaElement, state: AutocompleteState) {
    const text = textarea.value;
    const before = text.substring(0, state.searchStartPos);
    const after = text.substring(textarea.selectionStart);
    const reference = `@[roleId:${role.id}]`;

    textarea.value = before + reference + after;

    // Move cursor after the reference
    const newCursorPos = before.length + reference.length;
    textarea.selectionStart = newCursorPos;
    textarea.selectionEnd = newCursorPos;

    // Trigger input event for any listeners
    textarea.dispatchEvent(new Event('input', { bubbles: true }));

    hideDropdown();
}

/**
 * Get caret coordinates within textarea
 */
function getCaretCoordinates(textarea: HTMLTextAreaElement): { top: number; left: number } {
    // Simple approximation - for production, could use a library like textarea-caret
    const style = window.getComputedStyle(textarea);
    
    // lineHeight might be 'normal' or other non-numeric value
    let lineHeight = parseInt(style.lineHeight);
    if (isNaN(lineHeight)) {
        const fontSize = parseInt(style.fontSize) || 14;
        lineHeight = fontSize * 1.5; // Default line-height is typically 1.2-1.5x font size
    }

    return {
        top: lineHeight,
        left: 0
    };
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Update available roles (e.g., when roles are loaded or changed)
 */
export function updateAutocompleteRoles(roles: Role[]) {
    textareaStates.forEach((state) => {
        state.roles = roles;
    });
}
